import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { emptyFunding, formatMinor, minorToText, todayIso } from "../../lib/finance";
import { PaymentBadge } from "./FinanceBadges";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../ui/responsive-popover";
import { Building2, ChevronDown, Loader2, Plus, Trash2, UserRound, UsersRound } from "lucide-react";

// Explicit payer choice: Jamora, a collaborator the caller may name (everyone with finance:view_all,
// only their own fiche otherwise, same rule as the server), or another person/organisation typed by hand.
export function PayerPicker({ funding, onPick, index }) {
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState({ items: [], scope: "all" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/finance/payer-candidates", { params: { q: query.trim() || undefined } });
        if (!cancelled) setResult(data);
      } catch {
        if (!cancelled) setResult({ items: [], scope: "all" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, query]);

  const choose = (patch) => { onPick(patch); setOpen(false); setQuery(""); };
  const label = funding.payer_type === "company" ? "Jamora (l'entreprise)" : funding.payer_type === "collaborator" ? funding.collaborator_label || "Collaborateur"
    : funding.payer_type === "third_party" ? "Autre personne ou organisation" : "Choisir qui a payé";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal h-10" data-testid={`finance-payer-${index}`}
          aria-label={`Payeur du paiement ${index + 1}`}>
          <span className={`truncate ${funding.payer_type ? "text-slate-900" : "text-slate-500"}`}>{label}</span>
          <ChevronDown size={14} className="text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(22rem,calc(100vw-2rem))] p-2 space-y-1">
        <button type="button" onClick={() => choose({ payer_type: "company", collaborator_id: null, collaborator_label: "", third_party_name: "" })}
          className="w-full text-left px-2 py-2 rounded-md text-sm hover:bg-slate-100 flex items-center gap-2 disabled:opacity-40 disabled:hover:bg-transparent min-h-[44px] md:min-h-0"
          data-testid={`finance-payer-${index}-company`}>
          <Building2 size={15} className="text-slate-400 shrink-0" />
          <span>Jamora (l'entreprise)</span>
        </button>
        <div className="border-t border-slate-100 pt-2 space-y-1">
          <div className="px-2 text-xs font-medium text-slate-500">{result.scope === "own" ? "Vous-même" : "Un collaborateur"}</div>
          {result.scope === "all" && (
            <Input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher par nom ou référence"
              className="text-base md:text-sm" aria-label="Rechercher un collaborateur" data-testid={`finance-payer-${index}-search`} />
          )}
          <div className="max-h-48 overflow-y-auto" role="listbox" aria-label="Collaborateurs">
            {loading && <div className="flex justify-center py-2"><Loader2 className="animate-spin text-slate-400" size={16} /></div>}
            {!loading && !result.items.length && (
              <p className="px-2 py-2 text-xs text-slate-500">
                {result.scope === "own" ? "Votre compte n'est lié à aucune fiche Collaborateur : vous ne pouvez pas déclarer de paiement personnel." : "Aucun collaborateur trouvé."}
              </p>
            )}
            {!loading && result.items.map((p) => {
              return (
                <button key={p.id} type="button" role="option" aria-selected={funding.collaborator_id === p.id}
                  onClick={() => choose({ payer_type: "collaborator", collaborator_id: p.id, collaborator_label: p.display_name, third_party_name: "" })}
                  className="w-full text-left px-2 py-2 rounded-md text-sm hover:bg-slate-100 flex items-center gap-2 disabled:opacity-40 disabled:hover:bg-transparent min-h-[44px] md:min-h-0"
                  data-testid={`finance-payer-${index}-option-${p.id}`}>
                  <UserRound size={15} className="text-slate-400 shrink-0" />
                  <span className="min-w-0">
                    <span className="block truncate text-slate-900">{p.display_name}</span>
                    <span className="block text-xs text-slate-500">{p.reference}{p.archived_at ? " · archivé" : p.status === "exited" ? " · sorti" : ""}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="border-t border-slate-100 pt-1">
          <button type="button" onClick={() => choose({ payer_type: "third_party", collaborator_id: null, collaborator_label: "", third_party_name: funding.third_party_name || "" })}
            className="w-full text-left px-2 py-2 rounded-md text-sm hover:bg-slate-100 flex items-center gap-2 min-h-[44px] md:min-h-0"
            data-testid={`finance-payer-${index}-third`}>
            <UsersRound size={15} className="text-slate-400 shrink-0" />
            <span>Une autre personne ou organisation…</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FieldError({ children, id }) {
  return children ? <p id={id} className="text-xs text-red-700 mt-1">{children}</p> : null;
}

export default function FundingRows({ fundings, onChange, currency, evaluation, showErrors }) {
  const update = (index, patch) => onChange(fundings.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  const remove = (index) => onChange(fundings.filter((_, i) => i !== index));
  // The amount still unpaid is a sensible starting value; the payer and the date are never guessed.
  const add = () => onChange([...fundings, emptyFunding(evaluation.remainingMinor ? minorToText(evaluation.remainingMinor, currency).replace(".", ",") : "")]);
  const money = (minor) => (minor === null ? "Montant à saisir" : formatMinor(minor, currency));

  return (
    <div className="space-y-3" data-testid="finance-fundings">
      {!fundings.length ? (
        <p className="text-sm text-slate-600 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
          Pas encore payée ? Laissez cette section vide : la dépense sera enregistrée comme <strong>Non payée</strong>.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="hidden sm:grid grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,1fr)_40px] gap-2 px-1 text-xs font-medium text-slate-500">
            <span>Qui a payé</span><span>Montant payé</span><span>Date du paiement</span><span className="sr-only">Supprimer</span>
          </div>
          {fundings.map((f, i) => {
            const errors = showErrors ? evaluation.rowErrors[i] || {} : {};
            return (
              <div key={i} data-testid={`finance-funding-${i}`}
                className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_40px] sm:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,1fr)_40px] gap-2 items-start rounded-lg border border-slate-200 sm:border-0 p-2 sm:p-0">
                <div className="col-span-2 sm:col-span-1 min-w-0">
                  <span className="sm:hidden text-xs font-medium text-slate-500">Qui a payé</span>
                  <PayerPicker funding={f} index={i} onPick={(patch) => update(i, patch)} />
                  {f.payer_type === "third_party" && (
                    <Input value={f.third_party_name} onChange={(e) => update(i, { third_party_name: e.target.value })} maxLength={120}
                      placeholder="Nom de la personne ou de l'organisation" aria-label={`Nom du payeur ${i + 1}`}
                      className="mt-1.5 text-base md:text-sm" data-testid={`finance-third-name-${i}`} />
                  )}
                  <FieldError>{errors.payer}</FieldError>
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} aria-label={`Supprimer le paiement ${i + 1}`}
                  className="h-10 w-10 text-slate-500 hover:text-red-700 hover:bg-red-50 sm:order-last" data-testid={`finance-remove-funding-${i}`}>
                  <Trash2 size={16} />
                </Button>
                <div className="min-w-0">
                  <span className="sm:hidden text-xs font-medium text-slate-500">Montant payé</span>
                  <div className="relative">
                    <Input value={f.amount} onChange={(e) => update(i, { amount: e.target.value })} inputMode="decimal" placeholder="0"
                      aria-label={`Montant payé ${i + 1} en ${currency}`} aria-invalid={!!errors.amount}
                      className="pr-12 text-base md:text-sm tabular-nums" data-testid={`finance-funding-amount-${i}`} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">{currency}</span>
                  </div>
                  <FieldError>{errors.amount}</FieldError>
                </div>
                <div className="min-w-0">
                  <span className="sm:hidden text-xs font-medium text-slate-500">Date du paiement</span>
                  <Input type="date" max={todayIso()} value={f.paid_at} onChange={(e) => update(i, { paid_at: e.target.value })}
                    aria-label={`Date du paiement ${i + 1}`} aria-invalid={!!errors.paid_at}
                    className="text-base md:text-sm" data-testid={`finance-funding-date-${i}`} />
                  <FieldError>{errors.paid_at}</FieldError>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="finance-add-funding" disabled={fundings.length >= 30}>
        <Plus size={14} className="mr-1" /> {fundings.length ? "Ajouter un autre paiement" : "Ajouter un paiement"}
      </Button>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm" aria-live="polite" data-testid="finance-funding-totals">
        <span className="text-slate-500">Montant <strong className="text-slate-900 tabular-nums">{money(evaluation.totalMinor)}</strong></span>
        <span className="text-slate-500">Payé <strong className="text-slate-900 tabular-nums">{formatMinor(evaluation.paidMinor, currency)}</strong></span>
        <span className="text-slate-500">Reste à payer <strong className="text-slate-900 tabular-nums">{money(evaluation.remainingMinor)}</strong></span>
        <span className="sm:ml-auto"><PaymentBadge status={evaluation.status} /></span>
      </div>
      {evaluation.overpaid && <p role="alert" className="text-sm text-red-700">Le total payé dépasse le montant de la dépense.</p>}
    </div>
  );
}
